<script setup lang="ts">
import axios from 'axios';
import { computed, ref } from 'vue';
import ToolPageHead from '@/components/seo/ToolPageHead.vue';
import DropZone from '@/components/Tools/DropZone.vue';
import FileCard from '@/components/Tools/FileCard.vue';
import StatusMessage from '@/components/Tools/StatusMessage.vue';
import ToolLayout from '@/components/Tools/ToolLayout.vue';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { usePdfUploadLimit } from '@/composables/usePdfUploadLimit';
import { useToolPageSeoFromInertia } from '@/composables/useToolPageSeo';
import { useTranslations } from '@/composables/useTranslations';
import { dashboard } from '@/routes';
import { protect as protectPage } from '@/routes/tools/pdf';
import protectTool from '@/routes/tools/pdf/protect';

const { lineWithMax } = usePdfUploadLimit();
const { t } = useTranslations();

const seo = useToolPageSeoFromInertia({
    seoPrefix: 'tools.pdf.protect.seo',
    fallbackTitleKey: 'tools.pdf.protect.title',
    fallbackTitleLabel: 'Protect PDF',
    t,
    homeRelativeUrl: dashboard.url(),
    selfRelativeUrl: protectPage.url(),
});

defineOptions({
    layout: {
        breadcrumbs: [
            {
                translationKey: 'tools.nav.home',
                title: 'Home',
                href: dashboard.url(),
            },
            {
                translationKey: 'tools.pdf.protect.title',
                title: 'Protect PDF',
                href: protectPage.url(),
            },
        ],
    },
});

type Status = 'idle' | 'loading' | 'success' | 'error';

const file = ref<File | null>(null);
const userPassword = ref('');
const confirmPassword = ref('');
const ownerPassword = ref('');
const allowPrinting = ref(true);
const showAdvanced = ref(false);
const status = ref<Status>('idle');
const statusMessage = ref('');

const passwordMismatch = computed(
    () =>
        confirmPassword.value.length > 0 &&
        userPassword.value !== confirmPassword.value,
);

function onFileSelected(files: File[]): void {
    file.value = files[0] ?? null;
    status.value = 'idle';
}

async function extractError(err: unknown): Promise<string> {
    const e = err as { response?: { data: Blob | object } };

    if (e.response?.data instanceof Blob) {
        try {
            const parsed = JSON.parse(await (e.response.data as Blob).text());
            const msgs = Object.values(parsed.errors ?? {}).flat();

            return (
                (msgs[0] as string) ||
                t('tools.pdf.ui.protection_failed', 'Protection failed.')
            );
        } catch {
            return t('tools.pdf.ui.protection_failed', 'Protection failed.');
        }
    }

    return t('tools.pdf.ui.protection_failed', 'Protection failed.');
}

async function protect(): Promise<void> {
    if (
        !file.value ||
        !userPassword.value ||
        passwordMismatch.value ||
        status.value === 'loading'
    ) {
        return;
    }

    status.value = 'loading';
    statusMessage.value = t('tools.pdf.ui.encrypting', 'Encrypting PDF...');

    const formData = new FormData();
    formData.append('file', file.value);
    formData.append('user_password', userPassword.value);

    if (ownerPassword.value) {
        formData.append('owner_password', ownerPassword.value);
    }

    formData.append('allow_printing', allowPrinting.value ? '1' : '0');

    try {
        const response = await axios.post(protectTool.submit.url(), formData, {
            responseType: 'blob',
        });

        const cd = (response.headers['content-disposition'] as string) ?? '';
        const match = cd.match(/filename="?([^"]+)"?/);
        const filename = match?.[1] ?? 'protected.pdf';

        const blobUrl = URL.createObjectURL(response.data as Blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(blobUrl);

        status.value = 'success';
        statusMessage.value = t(
            'tools.pdf.ui.protected_success',
            'PDF protected successfully!',
        );
        file.value = null;
        userPassword.value = '';
        confirmPassword.value = '';
        ownerPassword.value = '';
    } catch (err: unknown) {
        status.value = 'error';
        statusMessage.value = await extractError(err);
    }
}
</script>

<template>
    <ToolPageHead v-bind="seo" json-ld-head-key="ld-json-pdf-protect" />

    <ToolLayout
        :title="t('tools.pdf.protect.title', 'Protect PDF')"
        :subtitle="t('tools.pdf.protect.description', '')"
    >
        <div class="space-y-4">
            <div class="rounded-xl border border-border bg-card p-6 shadow-sm">
                <DropZone
                    accept="application/pdf"
                    :multiple="false"
                    :label="
                        t('tools.pdf.ui.drop_pdf', 'Click or drag a PDF here')
                    "
                    :sublabel="
                        lineWithMax(t('tools.pdf.ui.pdf_file', 'PDF file'))
                    "
                    @update:model-value="onFileSelected"
                />
                <div v-if="file" class="mt-3">
                    <FileCard
                        :file="file"
                        :show-drag-handle="false"
                        @remove="file = null"
                    />
                </div>
            </div>

            <div class="rounded-xl border border-border bg-card p-6 shadow-sm">
                <div class="space-y-4">
                    <div>
                        <label
                            for="userPassword"
                            class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                            {{
                                t(
                                    'tools.pdf.ui.protect_password_label',
                                    'Password',
                                )
                            }}
                        </label>
                        <input
                            id="userPassword"
                            v-model="userPassword"
                            type="password"
                            :placeholder="
                                t(
                                    'tools.pdf.ui.enter_password',
                                    'Enter password',
                                )
                            "
                            class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-500 focus:outline-none dark:border-border dark:bg-muted dark:text-foreground"
                        />
                    </div>

                    <div>
                        <label
                            for="confirmPassword"
                            class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                            {{
                                t(
                                    'tools.pdf.ui.protect_confirm_label',
                                    'Confirm Password',
                                )
                            }}
                        </label>
                        <input
                            id="confirmPassword"
                            v-model="confirmPassword"
                            type="password"
                            :placeholder="
                                t(
                                    'tools.pdf.ui.repeat_password',
                                    'Repeat password',
                                )
                            "
                            class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:outline-none dark:border-border dark:bg-muted dark:text-foreground"
                            :class="
                                passwordMismatch
                                    ? 'border-red-400 focus:border-red-400'
                                    : 'focus:border-brand-500 dark:border-border'
                            "
                        />
                        <p
                            v-if="passwordMismatch"
                            class="mt-1 text-xs text-red-500"
                        >
                            {{
                                t(
                                    'tools.pdf.ui.protect_passwords_mismatch',
                                    'Passwords do not match.',
                                )
                            }}
                        </p>
                    </div>

                    <label
                        class="flex cursor-pointer items-center gap-3.5 select-none"
                    >
                        <Checkbox v-model="allowPrinting" class="shrink-0" />
                        <span
                            class="text-sm text-gray-700 dark:text-gray-300"
                            >{{
                                t(
                                    'tools.pdf.ui.protect_allow_printing',
                                    'Allow printing',
                                )
                            }}</span
                        >
                    </label>

                    <div>
                        <button
                            type="button"
                            class="text-sm text-brand-600 hover:underline dark:text-brand-400"
                            @click="showAdvanced = !showAdvanced"
                        >
                            {{
                                showAdvanced
                                    ? t(
                                          'tools.pdf.ui.protect_hide_advanced',
                                          'Hide advanced options',
                                      )
                                    : t(
                                          'tools.pdf.ui.protect_show_advanced',
                                          'Show advanced options',
                                      )
                            }}
                        </button>

                        <div v-if="showAdvanced" class="mt-3">
                            <label
                                for="ownerPassword"
                                class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
                            >
                                {{
                                    t(
                                        'tools.pdf.ui.protect_owner_password_label',
                                        'Owner Password',
                                    )
                                }}
                                <span class="font-normal text-gray-500">{{
                                    t(
                                        'tools.pdf.ui.protect_owner_password_hint',
                                        '(optional — controls edit permissions)',
                                    )
                                }}</span>
                            </label>
                            <input
                                id="ownerPassword"
                                v-model="ownerPassword"
                                type="password"
                                :placeholder="
                                    t(
                                        'tools.pdf.ui.permissions_password_placeholder',
                                        'Leave blank to use the same password',
                                    )
                                "
                                class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-500 focus:outline-none dark:border-border dark:bg-muted dark:text-foreground"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <Button
                class="w-full bg-brand-600 text-white hover:bg-brand-700"
                :disabled="
                    !file ||
                    !userPassword ||
                    passwordMismatch ||
                    status === 'loading'
                "
                @click="protect"
            >
                {{ t('tools.pdf.ui.protect_button', 'Protect PDF') }}
            </Button>

            <StatusMessage :status="status" :message="statusMessage" />
        </div>
    </ToolLayout>
</template>
